import React, { FC, useContext, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { LinearProgress } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Box, Button, Grid, Step, StepLabel, Stepper, Typography } from '@material-ui/core';

import './Tutorial.scss';
import { isPhone } from '../../services/deviceService';
import ReactGA from "react-ga4";
import TenantContext from '../../contexts/tenantContext';

interface TutorialProps {
  next(): void;
}

enum StepState {
  Message,
  Video,
  KeyPoints,
  Timeout,
}

const Tutorial: FC<TutorialProps> = props => {
  const [activeStep, setActiveStep] = React.useState(() => {
    const storedStep = localStorage.getItem('activeTutorialStep');
    return storedStep ? parseInt(storedStep, 10) : 0;
  });

  const [activeStepState, setActiveStepState] = React.useState(() => {
    const storedStep = localStorage.getItem('activeTutorialStep');
    if (storedStep && parseInt(storedStep, 10) >= 1) {
      return StepState.KeyPoints;
    }
    return StepState.Message
  });
  const [resizeToggle, setResized] = React.useState(false);

  const [videoWatched, setVideoWatched] = React.useState(true);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const tenant = useContext(TenantContext);

  useEffect(() => {
    ReactGA.event({
      category: 'Progress',
      action: 'Tutorial Step Changed',
      label: activeStep.toString()
    });
    localStorage.setItem('activeTutorialStep', activeStep.toString());
  }, [activeStep]);

  useEffect(() => {

    if (activeStepState === StepState.Video &&  videoRef?.current){
      videoRef?.current?.play();
    }
  },[activeStepState])

  const showPhone = useMediaQuery('(max-width:860px)');
  const isPhonePx = useMediaQuery('(max-width:560px)');

  const handleResize = () => {
    setResized(!resizeToggle);
  }

  const getStepContent = (step: number) => {
    if (isPhone() || showPhone) {
      return tenant.tutorialPhoneSteps[Math.abs(step)];
    }
    return tenant.tutorialSteps[Math.abs(step)];
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    // eslint-disable-next-line
  }, []);

  const onVideoEnded = () => {
    setActiveStepState(state => StepState.KeyPoints);
    setVideoWatched(true);
  }

  const handleNext = () => {

    if (activeStep === tenant.tutorialSteps.length - 1)
      props.next();

    if (activeStepState === StepState.KeyPoints || activeStepState === StepState.Video) {

      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setActiveStepState(() => StepState.Timeout);

      setTimeout(() => {
        setActiveStepState(() => getStepContent(activeStep + 1)?.message
          ? StepState.Message
          : StepState.Video);
      }, 150);
    }
    else {
      setActiveStepState(() => StepState.Video);
    }

    setVideoWatched(false);
  };

  const handleRepeat = () => {
    videoRef!.current!.currentTime = 0;
    setVideoWatched(false);
    setActiveStepState(() => StepState.Video);
    videoRef?.current?.play();
  }

  return (
    <>
      {tenant.tutorialSteps && <div className="tutorial">
        {(isPhone() || isPhonePx) ? <div>
          <LinearProgress className='phone-tutorial-progress' variant="determinate" value={(activeStep + 1) * 20} />
        </div> :
          <Stepper activeStep={activeStep} className="tutorial-stepper">
            {tenant.tutorialSteps.map((t, index) => {
              return (
                <Step key={index}>
                  <StepLabel></StepLabel>
                </Step>
              );
            })}
          </Stepper>
        }
        <div>
          {activeStep === tenant.tutorialSteps.length ? (
            <div>
              All steps completed - you&apos;re finished
            </div>
          ) : (
            <div className="tutorial-content-container">
              {activeStepState === StepState.Message &&
                <Grid container direction="row" alignItems="center" justifyContent="center">
                  <Grid className="message-container">
                    <Box height="20vh"></Box>
                    <Typography variant="h5" className="message"><span dangerouslySetInnerHTML={{ __html: getStepContent(activeStep).message || "" }} /></Typography>
                    <Box height="20vh"></Box>
                  </Grid>
                </Grid>
                }

                {(activeStepState === StepState.KeyPoints || activeStepState === StepState.Video) &&
                  getStepContent(activeStep).videoUrl &&
                  <video
                    ref={videoRef}
                    playsInline
                    controls
                    // onPlay={handleStart}
                    onEnded={onVideoEnded}
                    controlsList='nodownload nofullscreen noremoteplayback'
                    disablePictureInPicture
                    style={activeStepState === StepState.KeyPoints ? { opacity: '0.05', transition: "opacity 500ms" } : { opacity: '100%' }}
                  >
                    <source src={getStepContent(activeStep).videoUrl} type="video/mp4" />
                    <track
                      kind="subtitles"
                      src={`/subtitles/${tenant.tenantName}/tutorial/t${activeStep}.en.vtt`}
                      srcLang="en"
                      default
                    />
                    Your browser does not support the video tag.
                  </video>
                }

                {activeStepState === StepState.KeyPoints &&
                <div className="key-points-abs-container">
                  <div>
                    <div className="key-points-container">
                      <div>
                        <h2>Video tutorial {activeStep + 1}</h2>
                        <h3>Key points</h3>
                        <div className="text-container">
                          <p dangerouslySetInnerHTML={{ __html: getStepContent(activeStep).keyPoints }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              }
                <div className="video-buttons-row">
                  <div className='overlay'>
                    {activeStepState === StepState.Video &&
                      <Button
                        className="green-button skip-tutorial-button"
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          try {
                            videoRef!.current!.currentTime = videoRef!.current!.duration;
                            // videoRef.current?.pause()
                          } catch (e) {
                            console.log('skip error', e)
                          }
                        }}
                      >
                        Skip
                      </Button>}
                    {videoWatched &&
                      <Button
                        className="green-button"
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                      >
                        I understand
                      </Button>
                    }
                    {videoWatched && activeStepState > 0 &&
                      <Button
                        className="tutorial-repeat-button green-button"
                        variant="contained"
                        color="primary"
                        onClick={handleRepeat}
                      >
                        Repeat
                      </Button>
                    }
                  </div>
                </div>
            </div>
          )}
        </div>
      </div>}
    </>
  );
}

export default Tutorial;
